/**
 * In-memory queue of directives waiting to be injected into a chat.
 * Durability comes from the server's `claim-terminal` record (survives reload)
 * and the committed message list (records delivery). Stranded directives are
 * re-derived on load by `findStrandedTerminalEntries`.
 *
 * `pendingInjectionsAtom` is a plain in-session atom, per-tab: there is no
 * shared state for a cross-tab lock to serialize (ADR 059).
 */
import React from "react";
import { atom, useAtom, useSetAtom } from "jotai";

import * as types from "types";
import type { PendingInjectionKind } from "types/longTask";
import type { TaskStatusValue } from "types/TaskRegistry";

export type { PendingInjectionKind };

// Kinds deduped on the composite (longTaskJobId, kind) at both gates: the
// enqueue gate (mergePendingInjection, against the queue) and the drain gate
// (planInjectionDrain, against the committed message list). Both listed kinds are
// auto-generated (poller, sweep, peer tabs), so one-per-(job, purpose) is
// correct. User-initiated kinds are absent, so they dedup by `id` only and pass
// through both gates untouched, and a deliberate repeat click is never swallowed.
const COMPOSITE_DEDUP_KINDS: ReadonlySet<PendingInjectionKind> = new Set([
  "long-task-terminal",
  "long-task-fetch-result",
  "mcp-task-terminal",
]);

/** True when `kind` participates in composite (longTaskJobId, kind) dedup. */
function isCompositeDedupKind(kind?: PendingInjectionKind): boolean {
  return !!kind && COMPOSITE_DEDUP_KINDS.has(kind);
}

/**
 * True when both directives name the same (longTaskJobId, kind). False when `a`
 * lacks either field, so a non-long-task injection falls back to `id` dedup.
 */
function sameLongTaskDirective(
  a: { longTaskJobId?: string; kind?: PendingInjectionKind },
  b: { longTaskJobId?: string; kind?: PendingInjectionKind }
): boolean {
  if (!a.longTaskJobId || !a.kind) return false;
  return a.longTaskJobId === b.longTaskJobId && a.kind === b.kind;
}

export interface PendingInjection {
  id: string;
  chatId: string;
  content: string;
  createdAt: string;
  // Optional because the queue mixes two origins: auto-generated long-task
  // directives carry both and dedup on the composite (longTaskJobId, kind);
  // other injections omit one or both and dedup by `id`.
  longTaskJobId?: string;
  kind?: PendingInjectionKind;
  status?: TaskStatusValue;
  statusReason?: string;
}

export type PendingInjectionsByChat = Record<string, PendingInjection[]>;

export const pendingInjectionsAtom = atom<PendingInjectionsByChat>({});

/**
 * Adds an injection to the queue map, deduping on (longTaskJobId, kind) for
 * composite-dedup kinds and on `id` otherwise. Returns the same reference on
 * duplicate so callers can no-op. Must be called inside a Jotai functional
 * updater for atomic application.
 */
export function mergePendingInjection(
  current: PendingInjectionsByChat,
  chatId: string,
  injection: PendingInjection
): PendingInjectionsByChat {
  const chatQueue = current[chatId] ?? [];
  // Dedup by (longTaskJobId, kind) only for auto-generated long-task directives;
  // otherwise by `id`, so a repeat user click always enqueues.
  const useCompositeDedup =
    !!injection.longTaskJobId && isCompositeDedupKind(injection.kind);
  const isDuplicate = chatQueue.some((entry) => {
    if (useCompositeDedup && sameLongTaskDirective(injection, entry)) {
      return true;
    }
    return entry.id === injection.id;
  });
  if (isDuplicate) return current;
  return { ...current, [chatId]: [...chatQueue, injection] };
}

/**
 * Enqueue callback handed to the terminal helpers so they never name the queue
 * atom: they are plain async functions with no hooks available, and a test can
 * pass a spy. Implementations must apply an atomic functional update (see
 * `mergePendingInjection`): setting a freshly-built map wholesale would clobber
 * other chats' queues and concurrent enqueues.
 */
export type EnqueueInjection = (
  chatId: string,
  injection: PendingInjection
) => void;

/**
 * Builds an {@link EnqueueInjection} over an atom setter, for `makeTaskCacheOps`,
 * which is a plain function and cannot call hooks.
 */
export function makeEnqueueInjection(
  setPendingInjections: (
    updater: (prev: PendingInjectionsByChat) => PendingInjectionsByChat
  ) => void
): EnqueueInjection {
  return (chatId, injection) => {
    setPendingInjections((prev) =>
      mergePendingInjection(prev, chatId, injection)
    );
  };
}

/** Stable {@link EnqueueInjection} for components and hooks that enqueue directly. */
export function useEnqueueInjection(): EnqueueInjection {
  const setPendingInjections = useSetAtom(pendingInjectionsAtom);
  return React.useMemo(
    () => makeEnqueueInjection(setPendingInjections),
    [setPendingInjections]
  );
}

export function peekPendingInjectionsForChat(
  byChat: PendingInjectionsByChat,
  chatId: string
): PendingInjection[] {
  return byChat[chatId] ?? [];
}

/**
 * Drain decision given the peeked queue and the committed message list.
 * Returns `duplicateIds` for injections already committed (matched by
 * `(longTaskJobId, kind)`) and `next` as the first fresh injection.
 *
 * Only composite-dedup kinds are matched against the committed message list;
 * user-initiated kinds pass through so a repeat click is honored, mirroring the
 * enqueue gate.
 */
export function planInjectionDrain(
  peeked: PendingInjection[],
  committedMessages: types.Message[]
): { duplicateIds: string[]; next: PendingInjection | null } {
  const isAlreadyCommitted = (injection: PendingInjection): boolean => {
    if (!injection.longTaskJobId || !isCompositeDedupKind(injection.kind)) {
      return false;
    }
    const jobId = injection.longTaskJobId;
    return committedMessages.some((m) => {
      if (
        types.isSyntheticUserMessage(m) &&
        sameLongTaskDirective(injection, m)
      ) {
        return true;
      }
      // Backstop: the agent may have already called get_task_result for this
      // task on its own (e.g. the user asked directly), with no synthetic
      // directive in the message list to match against above.
      //
      // Only `mcp-task-terminal` can match. For that kind `longTaskJobId` holds
      // the chat API's managed-task id, which is exactly what `get_task_result`
      // takes as its `task_id`. The long-task kinds carry the upstream `job_id`
      // instead, a different identifier from a different system, so comparing the
      // two could only ever be false.
      if (
        injection.kind === "mcp-task-terminal" &&
        types.isToolCallMessage(m) &&
        m.tool_name.includes("get_task_result")
      ) {
        return types.readToolInputString(m, "task_id") === jobId;
      }
      return false;
    });
  };
  const duplicateIds: string[] = [];
  const fresh: PendingInjection[] = [];
  for (const injection of peeked) {
    if (isAlreadyCommitted(injection)) {
      duplicateIds.push(injection.id);
    } else {
      fresh.push(injection);
    }
  }
  return { duplicateIds, next: fresh[0] ?? null };
}

/** Removes injections by id from the map. */
export function clearPendingInjectionsForChat(
  byChat: PendingInjectionsByChat,
  chatId: string,
  ids: string[]
): PendingInjectionsByChat {
  const current = byChat[chatId];
  if (!current || current.length === 0) return byChat;
  const idSet = new Set(ids);
  const remaining = current.filter((entry) => !idSet.has(entry.id));
  const next = { ...byChat };
  if (remaining.length === 0) {
    delete next[chatId];
  } else {
    next[chatId] = remaining;
  }
  return next;
}

/** Drops a chat's entire pending-injection queue. */
export function removePendingInjectionsForChat(
  byChat: PendingInjectionsByChat,
  chatId: string
): PendingInjectionsByChat {
  if (!(chatId in byChat)) return byChat;
  const next = { ...byChat };
  delete next[chatId];
  return next;
}

/** Hook accessor for the pending-injection queue. */
export function usePendingInjections() {
  const [byChat, setByChat] = useAtom(pendingInjectionsAtom);

  const peek = React.useCallback(
    (chatId: string): PendingInjection[] =>
      peekPendingInjectionsForChat(byChat, chatId),
    [byChat]
  );

  const acknowledge = React.useCallback(
    (chatId: string, ids: string[]): void => {
      if (ids.length === 0) return;
      setByChat((prev) => clearPendingInjectionsForChat(prev, chatId, ids));
    },
    [setByChat]
  );

  return { byChat, peek, acknowledge };
}

/**
 * Converts a queued injection into a Message. Copies `longTaskJobId` and `kind`
 * so `hasCommittedTerminalDirective` can recognize the committed directive on
 * reload, which is what keeps `findStrandedTerminalEntries` from re-announcing a
 * job that was already announced.
 */
export function injectionToMessage(
  injection: PendingInjection
): types.SyntheticUserMessage {
  const message: types.SyntheticUserMessage = {
    uuid: crypto.randomUUID(),
    role: "user",
    directive: true,
    hide: true,
    content: injection.content,
  };
  if (injection.longTaskJobId !== undefined) {
    message.longTaskJobId = injection.longTaskJobId;
  }
  if (injection.kind !== undefined) {
    message.kind = injection.kind;
  }
  if (injection.status !== undefined) {
    message.status = injection.status;
  }
  if (injection.statusReason !== undefined) {
    message.statusReason = injection.statusReason;
  }
  return message;
}
